import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTicketAlt, FaClock, FaCheckCircle, FaTimesCircle, FaEye, FaPaperclip } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api/config';

const UserTicketHistory = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    fetchUserTickets();
  }, []);

  const fetchUserTickets = async () => {
    try {
      const response = await api.get('/support/tickets');
      if (response.data.success) {
        setTickets(response.data.tickets);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load your support tickets');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <FaClock color="#f39c12" />;
      case 'in-progress': return <FaClock color="#3498db" />;
      case 'waiting-for-user': return <FaClock color="#9b59b6" />;
      case 'resolved': return <FaCheckCircle color="#27ae60" />;
      case 'closed': return <FaTimesCircle color="#95a5a6" />;
      default: return <FaTicketAlt color="#34495e" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#e74c3c';
      case 'in-progress': return '#f39c12';
      case 'waiting-for-user': return '#9b59b6';
      case 'resolved': return '#27ae60';
      case 'closed': return '#95a5a6';
      default: return '#34495e';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return '#27ae60';
      case 'medium': return '#f39c12';
      case 'high': return '#e67e22';
      case 'critical': return '#e74c3c';
      default: return '#34495e';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading your support tickets...</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>My Support Tickets</Title>
        <Subtitle>View and track all your support requests</Subtitle>
      </Header>

      {tickets.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <FaTicketAlt />
          </EmptyIcon>
          <EmptyTitle>No Support Tickets Yet</EmptyTitle>
          <EmptyText>
            You haven't submitted any support tickets yet. If you need help,
            visit the <a href="/support">Support page</a> to submit a request.
          </EmptyText>
        </EmptyState>
      ) : (
        <TicketsGrid>
          {tickets.map(ticket => (
            <TicketCard key={ticket.id}>
              <TicketHeader>
                <TicketTitleWrapper>
                  <StatusIconWrapper>
                    {getStatusIcon(ticket.status)}
                  </StatusIconWrapper>
                  <TicketTitle>Ticket #{ticket.id}: {ticket.subject}</TicketTitle>
                </TicketTitleWrapper>
                <TicketMeta>
                  <PriorityBadge color={getPriorityColor(ticket.priority)}>
                    {ticket.priority.toUpperCase()}
                  </PriorityBadge>
                  <StatusBadge color={getStatusColor(ticket.status)}>
                    {ticket.status.replace('-', ' ').toUpperCase()}
                  </StatusBadge>
                </TicketMeta>
              </TicketHeader>

              <TicketInfo>
                <InfoItem>
                  <strong>Category:</strong> {ticket.category.charAt(0).toUpperCase() + ticket.category.slice(1)}
                </InfoItem>
                <InfoItem>
                  <strong>Created:</strong> {formatDate(ticket.createdAt)}
                </InfoItem>
                <InfoItem>
                  <strong>Last Updated:</strong> {formatDate(ticket.updatedAt)}
                </InfoItem>
                {ticket.attachments && ticket.attachments.length > 0 && (
                  <InfoItem>
                    <strong>Attachments:</strong> {ticket.attachments.length} file(s)
                    <FaPaperclip style={{ marginLeft: '5px' }} />
                  </InfoItem>
                )}
              </TicketInfo>

              <TicketDescription>
                {ticket.description.length > 150
                  ? `${ticket.description.substring(0, 150)}...`
                  : ticket.description
                }
              </TicketDescription>

              <TicketActions>
                <ActionButton onClick={() => setSelectedTicket(ticket)}>
                  <FaEye /> View Details
                </ActionButton>
              </TicketActions>
            </TicketCard>
          ))}
        </TicketsGrid>
      )}

      {selectedTicket && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Ticket #{selectedTicket.id}: {selectedTicket.subject}</ModalTitle>
              <CloseButton onClick={() => setSelectedTicket(null)}>×</CloseButton>
            </ModalHeader>

            <ModalBody>
              <TicketDetails>
                <DetailSection>
                  <DetailLabel>Status:</DetailLabel>
                  <DetailValue>
                    <StatusBadge color={getStatusColor(selectedTicket.status)}>
                      {selectedTicket.status.replace('-', ' ').toUpperCase()}
                    </StatusBadge>
                  </DetailValue>
                </DetailSection>
                <DetailSection>
                  <DetailLabel>Priority:</DetailLabel>
                  <DetailValue>
                    <PriorityBadge color={getPriorityColor(selectedTicket.priority)}>
                      {selectedTicket.priority.toUpperCase()}
                    </PriorityBadge>
                  </DetailValue>
                </DetailSection>
                <DetailSection>
                  <DetailLabel>Category:</DetailLabel>
                  <DetailValue>{selectedTicket.category.charAt(0).toUpperCase() + selectedTicket.category.slice(1)}</DetailValue>
                </DetailSection>
                <DetailSection>
                  <DetailLabel>Created:</DetailLabel>
                  <DetailValue>{formatDate(selectedTicket.createdAt)}</DetailValue>
                </DetailSection>
                <DetailSection>
                  <DetailLabel>Last Updated:</DetailLabel>
                  <DetailValue>{formatDate(selectedTicket.updatedAt)}</DetailValue>
                </DetailSection>
              </TicketDetails>

              <DescriptionSection>
                <SectionTitle>Description:</SectionTitle>
                <DescriptionText>{selectedTicket.description}</DescriptionText>
              </DescriptionSection>

              {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                <AttachmentsSection>
                  <SectionTitle>Attachments:</SectionTitle>
                  <AttachmentsList>
                    {selectedTicket.attachments.map((attachment, index) => (
                      <AttachmentItem key={index}>
                        <AttachmentName>{attachment.filename}</AttachmentName>
                        <AttachmentSize>({Math.round(attachment.size / 1024)} KB)</AttachmentSize>
                      </AttachmentItem>
                    ))}
                  </AttachmentsList>
                </AttachmentsSection>
              )}

              <ResponsesSection>
                <SectionTitle>Responses:</SectionTitle>
                {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                  <ResponsesList>
                    {selectedTicket.responses.map(response => (
                      <ResponseItem key={response.id}>
                        <ResponseHeader>
                          <ResponseAuthor>{response.adminName} (Support Team)</ResponseAuthor>
                          <ResponseDate>{formatDate(response.createdAt)}</ResponseDate>
                        </ResponseHeader>
                        <ResponseMessage>{response.message}</ResponseMessage>
                      </ResponseItem>
                    ))}
                  </ResponsesList>
                ) : (
                  <NoResponses>
                    No responses yet. Our support team will respond to your ticket soon.
                  </NoResponses>
                )}
              </ResponsesSection>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 50px;
  font-size: 18px;
  color: #666;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 28px;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 16px;
  margin: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: #bdc3c7;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 20px;
`;

const EmptyText = styled.p`
  color: #666;
  line-height: 1.5;
  margin: 0;

  a {
    color: #3498db;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TicketsGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const TicketCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 20px;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  }
`;

const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
`;

const TicketTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusIconWrapper = styled.div`
  font-size: 20px;
`;

const TicketTitle = styled.h3`
  color: #2c3e50;
  margin: 0;
  font-size: 18px;
`;

const TicketMeta = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const PriorityBadge = styled.span`
  background: ${props => props.color};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const StatusBadge = styled.span`
  background: ${props => props.color};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const TicketInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
`;

const InfoItem = styled.div`
  font-size: 14px;
  color: #555;
  display: flex;
  align-items: center;
`;

const TicketDescription = styled.p`
  color: #666;
  margin-bottom: 15px;
  line-height: 1.5;
`;

const TicketActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.2s;

  &:hover {
    background: #2980b9;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const TicketDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const DetailSection = styled.div``;

const DetailLabel = styled.div`
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 5px;
`;

const DetailValue = styled.div`
  color: #666;
`;

const DescriptionSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 18px;
`;

const DescriptionText = styled.p`
  color: #666;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const AttachmentsSection = styled.div`
  margin-bottom: 20px;
`;

const AttachmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const AttachmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const AttachmentName = styled.span`
  color: #2c3e50;
  font-weight: 500;
`;

const AttachmentSize = styled.span`
  color: #666;
  font-size: 14px;
`;

const ResponsesSection = styled.div``;

const ResponsesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ResponseItem = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  border-left: 4px solid #3498db;
`;

const ResponseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ResponseAuthor = styled.span`
  font-weight: 500;
  color: #2c3e50;
`;

const ResponseDate = styled.span`
  color: #666;
  font-size: 14px;
`;

const ResponseMessage = styled.p`
  color: #555;
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
`;

const NoResponses = styled.p`
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 20px;
`;

export default UserTicketHistory;